import store from '@/store'
import useNotifications from '@/composables/useNotifications'
import { useRouter } from '@core/utils/utils'
import { ref } from '@vue/composition-api'
// import { queryProcess, updateProcess } from '@/@core/queries/process'
import i18n from '@/libs/i18n'
import { useLambda } from '@/views/habit/awsLambda'
import { oid } from '@/helpers/mongoHelpers'
import { hasPermission } from '@/composables/usePermission'
import useAuditLog from '@/composables/useAuditLog'

export default function useProcessEdit() {
  const { showSuccessMessage, showErrorMessage } = useNotifications()
  const { router, route } = useRouter()
  const { getItemsAggregate, updateItem } = useLambda()
  const { addAuditEntry } = useAuditLog()
  const userData = store.state?.userStore?.userData
  const userObjectId = userData.worker_id != null ? oid(userData.worker_id.$oid) : null
  const item = ref(null)
  const originalData = ref(null)
  const isSubmitting = ref(false)
  const query = {
    _id: oid(route.value.params.id),
    client_id: oid(userData.client.$oid),
    deleted: { $ne: true },
  }
  if (!hasPermission('risks', 'edit')) {
    query.$or = [
      { responsibles: userObjectId },
      { leadResponsibles: userObjectId },
      { evaluator: userObjectId },
    ]
  }
  const collection = 'process'

  const show = async () => {
    isSubmitting.value = true

    try {
      const pipeline = [
        { $match: query },
        { $lookup: { from: 'crime', localField: 'crimes', foreignField: '_id', pipeline: [ { $project: { name: 1, law: 1, lawNumber: 1, article: 1 } } ], as: 'crimesData' } },
        { $lookup: { from: 'location', localField: 'areas', foreignField: '_id', pipeline: [ { $project: { location: 1 } } ], as: 'areasData' } },
        { $lookup: { from: 'worker', localField: 'responsibles', foreignField: '_id', pipeline: [ { $project: { name: 1 } } ], as: 'responsiblesData' } },
        { $lookup: { from: 'worker', localField: 'leadResponsibles', foreignField: '_id', pipeline: [ { $project: { name: 1 } } ], as: 'leadResponsiblesData' } },
        { $lookup: { from: 'worker', localField: 'evaluator', foreignField: '_id', pipeline: [ { $project: { name: 1 } } ], as: 'evaluatorData' } },
        { $lookup: { from: 'worker', localField: 'evaluated', foreignField: '_id', pipeline: [ { $project: { name: 1 } } ], as: 'evaluatedData' } },
        { $lookup: { from: 'company', localField: 'company', foreignField: '_id', pipeline: [ { $project: { name: 1 } } ], as: 'companyData' } },
        { $lookup: { from: 'id', localField: 'id', foreignField: '_id', pipeline: [ { $project: { name: 1 } } ], as: 'idData' } },
      ]
    
      const items = await getItemsAggregate({ collection, pipeline })

      if (!items[0]) {
        showErrorMessage(i18n.t('message.you_are_not_authorized'))
        router.push({ name: 'organization-process-list' })
        return
      }

      items[0].crimes = items[0].crimesData?.map(({ name, _id, law, lawNumber, article }) => {
        if (lawNumber) name += ` / ${lawNumber}`
        if (law) name += ` / ${law}`
        if (article) name += ` / ${article}`
        return { title: name, value: String(_id) }
      }) || []
      items[0].areas = items[0].areasData?.map(({ location, _id }) => ({ title: location, value: String(_id) })) || []
      items[0].controls = items[0].controls?.map(e => String(e)) || []
      items[0].responsibles = items[0].responsiblesData?.map(({ name, _id }) => ({ title: name, value: String(_id) })) || []
      items[0].leadResponsibles = items[0].leadResponsiblesData?.map(({ name, _id }) => ({ title: name, value: String(_id) })) || []
      items[0].evaluator = items[0].evaluatorData[0] ? { title: items[0].evaluatorData[0].name, value: String(items[0].evaluatorData[0]._id) } : null
      items[0].evaluated = items[0].evaluatedData[0] ? { title: items[0].evaluatedData[0].name, value: String(items[0].evaluatedData[0]._id) } : null
      items[0].residualRisk = items[0].residualRisk ? { title: i18n.t(`residualRisk.${items[0].residualRisk}`), value: items[0].residualRisk } : ''
      items[0].residualRiskProbability = items[0].residualRiskProbability ? { title: i18n.t(`residualRiskWithNumbers.${items[0].residualRiskProbability}`), value: items[0].residualRiskProbability } : ''
      items[0].residualRiskImpact = items[0].residualRiskImpact ? { title: i18n.t(`residualRiskWithNumbers.${items[0].residualRiskImpact}`), value: items[0].residualRiskImpact } : ''
      items[0].inherentRisk = items[0].inherentRisk ? { title: i18n.t(`residualRisk.${items[0].inherentRisk}`), value: items[0].inherentRisk } : ''
      items[0].inherentRiskProbability = items[0].inherentRiskProbability ? { title: i18n.t(`residualRiskWithNumbers.${items[0].inherentRiskProbability}`), value: items[0].inherentRiskProbability } : ''
      items[0].inherentRiskImpact = items[0].inherentRiskImpact ? { title: i18n.t(`residualRiskWithNumbers.${items[0].inherentRiskImpact}`), value: items[0].inherentRiskImpact } : ''
      items[0].company = items[0].companyData[0] ? { title: items[0].companyData[0].name, value: String(items[0].companyData[0]._id) } : null
      items[0].id = items[0].idData[0] ? { title: items[0].idData[0].name, value: String(items[0].idData[0]._id) } : null

      // Inicializar history si no existe (documentos legacy)
      if (!items[0].history) {
        items[0].history = []
      }

      item.value = items[0]
      // Store original data for change detection
      originalData.value = JSON.parse(JSON.stringify(items[0]))
    } catch (error) {
      console.log(error)
      showErrorMessage(i18n.t('message.data_fetch_error'))
    } finally {
      isSubmitting.value = false
    }
  }

  const update = async payload => {
    if (!hasPermission('risks', 'edit')) {
      showErrorMessage(i18n.t('message.you_are_not_authorized'))
      return
    }

    isSubmitting.value = true

    payload.crimes = payload.crimes?.map(e => oid(e.value)) || []
    payload.areas = payload.areas?.map(e => oid(e.value)) || []
    payload.controls = payload.controls?.map(e => oid(e)) || []
    payload.responsibles = payload.responsibles?.map(e => oid(e)) || []
    payload.leadResponsibles = payload.leadResponsibles?.map(e => oid(e)) || []
    payload.evaluator = payload.evaluator ? oid(payload.evaluator) : null
    payload.evaluated = payload.evaluated ? oid(payload.evaluated) : null
    payload.residualRisk = payload.residualRisk?.value || ''
    payload.residualRiskProbability = payload.residualRiskProbability?.value || ''
    payload.residualRiskImpact = payload.residualRiskImpact?.value || ''
    payload.inherentRisk = payload.inherentRisk?.value || ''
    payload.inherentRiskProbability = payload.inherentRiskProbability?.value || ''
    payload.inherentRiskImpact = payload.inherentRiskImpact?.value || ''
    payload.company = payload.company ? oid(payload.company) : null
    payload.id = payload.id ? oid(payload.id) : null

    try {
      const action = { $set: payload }

      await updateItem({ collection, query, action })

      // Add audit log entry (sistema unificado)
      await addAuditEntry({
        collection,
        documentId: oid(route.value.params.id),
        originalData: originalData.value,
        updatedData: payload,
      })

      showSuccessMessage(i18n.t('message.process_updated'))
    } catch (error) {
      console.log(error)
      showErrorMessage(i18n.t('message.process_update_error'))
    } finally {
      router.push({ name: 'organization-process-list' })
      isSubmitting.value = false
    }
  }

  return {
    update,
    show,
    item,
    isSubmitting,
  }
}
